export const SET_FETCH_ERROR = 'SET_FETCH_ERROR';
export const CLEAR_FETCH_ERROR = 'CLEAR_FETCH_ERROR';

export const DEFAULT_FETCH_ERROR =
  'Some data on this page could not be loaded. Please reload the page to try again. If this keeps happening, let us know using the feedback button.';

export const NOT_FOUND_MESSAGE = 'The requested data could not be found.';

function isNotFound(failure) {
  return Boolean(failure && failure.status === 404);
}

export function setFetchError(message, variant, link, key) {
  return { type: SET_FETCH_ERROR, message, variant, link, key };
}

export function clearFetchError(key) {
  return { type: CLEAR_FETCH_ERROR, key };
}

// Requests belong to the page that started them, identified by the navigation
// count at that moment. A counter, not a name: revisiting the same URL is a
// different page, so a failure from the earlier visit stays silent instead of
// landing on the later one.
let navigationCount = 0;

export function leavePage() {
  navigationCount += 1;
}

export function pageToken() {
  return navigationCount;
}

// A dispatch that remembers the page its operation started on, so a later
// request of the same operation cannot be mistaken for the destination's.
function pageOf(dispatch) {
  return dispatch.page !== undefined ? dispatch.page : navigationCount;
}

export function withPage(dispatch) {
  const paged = action => dispatch(action);
  paged.page = pageOf(dispatch);
  return paged;
}

// Retrying a request withdraws the banner its previous attempt raised, and
// nothing else. By default the two attempts are matched by their full URL,
// so an exact retry needs no wiring. A control that refetches a different
// URL into the same place on the page (a search box, a heatmap's score type)
// tags its dispatch with one logical key instead.
export function withRetryKey(dispatch, key) {
  const keyed = action => dispatch(action);
  keyed.retryKey = key;
  keyed.page = pageOf(dispatch);
  // One logical attempt may issue several requests. Only its first request
  // withdraws the banner; a later one could otherwise erase a newer attempt's
  // failure raised in between.
  let withdrawn = false;
  keyed.startRequest = () => {
    if (withdrawn) return;
    withdrawn = true;
    dispatch(clearFetchError(key));
  };
  return keyed;
}

// Relative /api/... URLs need a base to parse; only the path and query matter.
function urlKey(url) {
  // .invalid is reserved and never resolves; nothing requests this origin.
  const parsed = new URL(url, 'https://humanbase.invalid');
  return `${parsed.pathname}${parsed.search}`;
}

function keyFor(dispatch, args) {
  if (dispatch.retryKey) return dispatch.retryKey;
  const url = args.find(arg => typeof arg === 'string');
  return url ? urlKey(url) : undefined;
}

// For callers that split fetchResponse from readJsonResponse and need the
// same key on both halves.
export function retryKeyFor(url) {
  return urlKey(url);
}

export function isAbortError(error) {
  return Boolean(error && error.name === 'AbortError');
}

export function getFetchErrorMessage(failure) {
  if (isNotFound(failure)) return NOT_FOUND_MESSAGE;
  // The status code goes to the console and Sentry, not to the user.
  return DEFAULT_FETCH_ERROR;
}

// DRF puts the reason in "detail" or under the offending field; the first
// string in the body is the message worth showing.
export async function readErrorDetail(response) {
  try {
    const body = await response.json();
    return Object.values(body).find(value => typeof value === 'string');
  } catch (error) {
    return undefined;
  }
}

export function reportFetchError(
  dispatch,
  failure,
  key = dispatch.retryKey,
  page = pageOf(dispatch),
) {
  // Navigation aborts are expected and must not create an error banner.
  if (isAbortError(failure)) return;
  if (page !== navigationCount) return;
  dispatch(
    setFetchError(
      getFetchErrorMessage(failure),
      // A 404 answers the question rather than failing to, so it reads as
      // information about the data, not as something going wrong.
      isNotFound(failure) ? 'info' : 'danger',
      undefined,
      key,
    ),
  );
}

export async function fetchResponse(fetchFn, dispatch, ...args) {
  const key = keyFor(dispatch, args);
  const page = pageOf(dispatch);
  // A retry starts by withdrawing its own earlier verdict; the banner comes
  // back if this attempt fails as well.
  // A request from a page already left withdraws nothing on the destination.
  if (page === navigationCount) {
    if (dispatch.startRequest) dispatch.startRequest();
    else if (key) dispatch(clearFetchError(key));
  }
  try {
    return await fetchFn(...args);
  } catch (error) {
    reportFetchError(dispatch, error, key, page);
    return undefined;
  }
}

async function readResponse(response, dispatch, readBody, key, page) {
  if (!response) return undefined;
  // Error bodies often have a different shape; never pass them to data reducers.
  if (!response.ok) {
    reportFetchError(dispatch, response, key, page);
    return undefined;
  }

  try {
    return await readBody(response);
  } catch (error) {
    reportFetchError(dispatch, error, key, page);
    return undefined;
  }
}

export function readJsonResponse(
  response,
  dispatch,
  key = dispatch.retryKey,
  page = pageOf(dispatch),
) {
  return readResponse(response, dispatch, value => value.json(), key, page);
}

export async function fetchJson(fetchFn, dispatch, ...args) {
  const page = pageOf(dispatch);
  const response = await fetchResponse(fetchFn, dispatch, ...args);
  return readJsonResponse(response, dispatch, keyFor(dispatch, args), page);
}

export async function fetchText(fetchFn, dispatch, ...args) {
  const page = pageOf(dispatch);
  const response = await fetchResponse(fetchFn, dispatch, ...args);
  return readResponse(
    response,
    dispatch,
    value => value.text(),
    keyFor(dispatch, args),
    page,
  );
}
